/*
 * Copyright (C) 2025 SimpliCity Digital Inc - All Rights Reserved
 */
import React, { useEffect, useRef, useState } from "react";
import { isArrayEmpty } from "cms/utils/empty-exists";
import { ReactComponent as LeftArrow } from "cms/assets/icons/left-arrow-alt.svg";
import { ReactComponent as RightArrow } from "cms/assets/icons/right-arrow-alt.svg";
import { Button } from "@mui/material";
import { useWindowSize } from "cms/hooks/use-window-size";

const CarouselWithNav = (props) => {

    const carouselItemList = props?.carouselItemList;
    const carouselItemWidth = props?.carouselItemWidth;
    const renderCarouselItem = props?.renderCarouselItem;

    const carouselItemListRef = useRef(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const { width: screenWidth } = useWindowSize();

    const mobileBreakpoint = 480;
    const isDesktop = screenWidth > mobileBreakpoint;
    const maxScreenSize = 1400;
    const carouselItemGap = isDesktop ? 32 : 24;
    const totalCarouselItems = carouselItemList?.length || 0;
    const carouselContainerPadding = isDesktop ? 128 : 40;

    const carouselContainerWidth = Math.min(screenWidth, maxScreenSize) - carouselContainerPadding;
    const carouselItemsPerPage = Math.max(1, Math.floor(carouselContainerWidth / (carouselItemWidth + carouselItemGap)));
    const scrollAmount = (carouselItemWidth + carouselItemGap) * carouselItemsPerPage;

    const canScrollRight = currentPage < totalPages - 1;
    const canScrollLeft = currentPage > 0;
    const showPageControls = totalPages > 1;

    const [scrollLeft, setScrollLeft] = useState(0);

    useEffect(() => {
        const handleScroll = () => {
            setScrollLeft(carouselItemListRef.current?.scrollLeft);
        };
        const node = carouselItemListRef.current;
        if (node) {
            node.addEventListener("scroll", handleScroll);
        }
        return () => node?.removeEventListener("scroll", handleScroll);
    }, []);


    useEffect(() => {
        const pages = Math.ceil(totalCarouselItems / carouselItemsPerPage);
        setTotalPages(pages);
    }, [totalCarouselItems, carouselItemsPerPage]);

    const scrollToCard = (cardIndex) => {
        if (carouselItemListRef.current) {
            const targetScrollLeft = cardIndex * (carouselItemWidth + carouselItemGap);
            carouselItemListRef.current.scrollTo({ left: targetScrollLeft, behavior: "smooth" });
        }
    };

    const scrollToPage = (pageIndex) => {
        scrollToCard(pageIndex * carouselItemsPerPage);
    };

    useEffect(() => {
        let pageIndex = Math.ceil(scrollLeft / scrollAmount);
        pageIndex = Math.max(0, pageIndex);
        pageIndex = Math.min(pageIndex, totalPages - 1);
        setCurrentPage(pageIndex);
    }, [scrollLeft, scrollAmount, totalPages]);

    if (isArrayEmpty(carouselItemList)) {
        return null;
    }

    return (
        <div className='carousel-with-nav-container'>
            <div className='carousel-items' ref={carouselItemListRef} style={{
                gap: carouselItemGap
            }}
            >
                {carouselItemList.map((item, index) => (
                    <div key={index} className='carousel-with-nav-item' style={{
                        width: carouselItemWidth
                    }}
                    >
                        {renderCarouselItem(item, index)}
                    </div>
                ))}
            </div>
            <div className={`carousel-controls ${!showPageControls ? "hide" : ""}`}>
                <div className='carousel-dots'>
                    {Array.from({ length: totalPages }).map((_, index) => (
                        <span
                            key={`carousel-dot-${index}`}
                            className={`carousel-dot ${index === currentPage ? "active" : ""}`}
                        />
                    ))}
                </div>
                <div className='carousel-arrows'>
                    <Button
                        className={`scroll-arrow ${!canScrollLeft ? "disabled " : ""}`}
                        disabled={!canScrollLeft}
                        onClick={() => scrollToPage(currentPage - 1)}
                    >
                        <LeftArrow alt='left-arrow' />
                    </Button>
                    <Button
                        className={`scroll-arrow ${!canScrollRight ? "disabled " : ""}`}
                        disabled={!canScrollRight}
                        onClick={() => scrollToPage(currentPage + 1)}
                    >
                        <RightArrow alt='right-arrow' />
                    </Button>
                </div>
            </div>
        </div>
    );
};

CarouselWithNav.displayName = "CarouselWithNav";
export { CarouselWithNav };
